import { SellerUnitOne } from "@/components/sup/one";
import { SellerUnitOneSkeleton } from "@/components/sup/skeletons/seller-unit-one-skeleton";
import { SkeletonLoader } from "@/components/ui/skeleton";
import { useSupOne } from "@/hooks/use-sup-data";
import { usePropertyInteractionStore } from "@/stores/property-interaction";
import { MapItemSUP } from "@/types/search";
import { useEffect, useState } from "react";

interface PreferenceCarouselProps {
	supIds: string[];
	projectName?: string | null;
	onToggleFavorite?: (property: MapItemSUP) => void;
	onShortlist?: (property: MapItemSUP) => void;
	className?: string;
}

export function PreferenceCarousel({ supIds, className = "" }: PreferenceCarouselProps) {
	const [currentIndex, setCurrentIndex] = useState(0);
	const overlaySupId = usePropertyInteractionStore(state => state.overlaySupId);

	// Fetch SUP data for all IDs without initial data (preference context doesn't have map data)
	const { data: sups, loading, error } = useSupOne(supIds);

	// Sync currentIndex with overlaySupId from store
	useEffect(() => {
		if (overlaySupId && sups.length > 0) {
			const index = sups.findIndex(sup => sup._id === overlaySupId);
			if (index !== -1 && index !== currentIndex) {
				setCurrentIndex(index);
			}
		}
	}, [overlaySupId, sups, currentIndex]);

	if (loading && sups?.length === 0) {
		return (
			<div className={`w-full h-full ${className}`}>
				<div className="p-4 border-b border-gray-200">
					<SkeletonLoader height="20px" width="200px" />
				</div>
				<SellerUnitOneSkeleton isInModal />
			</div>
		);
	}

	if (error && sups?.length === 0) {
		return (
			<div className={`flex items-center justify-center h-64 ${className}`}>
				<div className="text-red-500">Error loading property details</div>
			</div>
		);
	}

	if (!sups || sups.length === 0) {
		return (
			<div className={`flex items-center justify-center h-64 ${className}`}>
				<div className="text-gray-500">No property data available</div>
			</div>
		);
	}

	const currentSup = sups[currentIndex];

	return (
		<div className={`w-full h-[90vh] flex flex-col ${className}`}>
			{/* Property content */}
			<div className="flex-1">
				<SellerUnitOne
					supOne={currentSup}
					className="border-0 shadow-none rounded-none h-full"
					isLoading={loading}
					isInModal={true}
					isInPreferenceCard={true}
				/>
			</div>
		</div>
	);
}
